import { supabase } from '@/integrations/supabase/client';
import { ImportModelType, ImportPreviewData, ImportConfig, ImportSession } from '@/types/import';

export interface ImportBatchResult {
  success: boolean;
  created: number;
  updated: number;
  errors: Array<{
    row: number;
    message: string;
    data: Record<string, any>;
  }>;
}

export interface ImportResult {
  sessionId: string;
  success: boolean;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  errorCount: number;
  errors: Array<{
    row: number;
    message: string;
    data: Record<string, any>;
  }>;
  duration: number;
}

class ImportAPI {
  // Helper function to create user-friendly error messages
  private createFriendlyErrorMessage(error: any, row: any, modelType: ImportModelType): string {
    if (!(error instanceof Error)) {
      return 'Unknown error occurred';
    }

    const errorMessage = error.message.toLowerCase();
    const itemName = row.Name || row.name || row.Title || row.title || 'item';

    // People-specific errors
    if (modelType === 'people') {
      if (errorMessage.includes('name is required')) {
        return 'Person name is required';
      }
      if (errorMessage.includes('person with id') && errorMessage.includes('not found')) {
        return `Person with ID ${row.ID || row.id} not found`;
      }
      if (errorMessage.includes('failed to assign roles')) {
        return `Failed to assign roles: ${error.message.split(':').slice(1).join(':').trim()}`;
      }
      if (errorMessage.includes('unknown roles')) {
        return error.message; // Keep the detailed role error message
      }
    }

    // Books-specific errors
    if (modelType === 'books') {
      if (errorMessage.includes('title is required')) {
        return 'Book title is required';
      }
      if (errorMessage.includes('book with id') && errorMessage.includes('not found')) {
        return `Book with ID ${row.ID || row.id} not found`;
      }
      if (errorMessage.includes('failed to link authors')) {
        return `Failed to link authors: ${error.message.split(':').slice(1).join(':').trim()}`;
      }
      if (errorMessage.includes('failed to link categories')) {
        return `Failed to link categories: ${error.message.split(':').slice(1).join(':').trim()}`;
      }
    }

    // Common database errors
    if (errorMessage.includes('duplicate key')) {
      if (errorMessage.includes('people_email_key')) {
        return `Email "${row.Email || row.email}" is already in use`;
      }
      return `${modelType === 'categories' ? 'Category' : 
               modelType === 'people' ? 'Person' : 
               modelType === 'books' ? 'Book' : 'Item'} "${itemName}" already exists`;
    }
    
    if (errorMessage.includes('violates not-null constraint')) {
      const field = errorMessage.match(/column "([^"]+)"/)?.[1];
      const friendlyField = field === 'avatar_url' ? 'Avatar URL' : 
                           field === 'cover_bg_color' ? 'Cover Background Color' :
                           field === 'reading_time' ? 'Reading Time' :
                           field || 'unknown';
      return `Required field "${friendlyField}" is missing`;
    }
    
    if (errorMessage.includes('violates foreign key constraint')) {
      if (modelType === 'summary_contents' && errorMessage.includes('book_id')) {
        return `Book "${row['Book Title'] || row.book_title || 'unknown'}" not found`;
      }
      if (modelType === 'chapters' && errorMessage.includes('book_id')) {
        return `Book "${row['Book Title'] || row.book_title || 'unknown'}" not found`;
      }
      if (modelType === 'people' && errorMessage.includes('people_to_roles')) {
        return 'Invalid role assignment - role may not exist';
      }
      return 'Referenced data not found - please check related fields';
    }
    
    if (errorMessage.includes('invalid input syntax')) {
      if (errorMessage.includes('integer')) {
        return 'Invalid number format - please use whole numbers only';
      }
      if (errorMessage.includes('numeric')) {
        return 'Invalid decimal number format';
      }
      if (errorMessage.includes('boolean')) {
        return 'Invalid true/false value - use "true" or "false"';
      }
      if (errorMessage.includes('uuid')) {
        return 'Invalid ID format';
      }
      return 'Invalid data format in one or more fields';
    }
    
    if (errorMessage.includes('value too long')) {
      return 'Text is too long for the field';
    }
    
    if (errorMessage.includes('check constraint')) {
      if (errorMessage.includes('rating')) {
        return 'Rating must be between 0 and 5';
      }
      return 'Data violates validation rules';
    }
    
    // Model-specific errors for chapters
    if (modelType === 'chapters') {
      if (errorMessage.includes('book title and language are required')) {
        return 'Both Book Title and Language are required for chapters';
      }
      if (errorMessage.includes('book not found')) {
        return `Book "${row['Book Title'] || row.book_title || 'unknown'}" not found. Please ensure the book exists before importing chapters.`;
      }
      if (errorMessage.includes('summary content not found')) {
        const bookTitle = row['Book Title'] || row.book_title || 'unknown';
        const language = row.Language || row.language || 'unknown';
        return `Summary content not found for book "${bookTitle}" in language "${language}". Please create the summary content first.`;
      }
    }

    // Specific validation errors
    if (errorMessage.includes('book not found')) {
      return error.message; // Keep the specific book not found message
    }
    
    // Network/connection errors
    if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
      return 'Network error - please check your connection and try again';
    }
    
    // Default to original message if it's already user-friendly
    if (error.message.length < 100 && !errorMessage.includes('pg_') && !errorMessage.includes('supabase')) {
      return error.message;
    }
    
    return 'Data validation failed - please check the row data';
  }

  // Create a new import session
  async createImportSession(
    filename: string,
    modelType: ImportModelType,
    totalRows: number
  ): Promise<string> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('uid', user.user.id)
      .single();

    if (profileError || !profile) {
      throw new Error('User profile not found');
    }

    const { data, error } = await supabase
      .from('import_sessions')
      .insert({
        filename: filename || 'unknown.xlsx',
        model_type: modelType,
        imported_by: profile.id,
        total_rows: totalRows || 0,
        // status defaults to 'failed' - will only be changed to 'completed' on success
      })
      .select('id')
      .single();

    if (error) {
      throw new Error(`Failed to create import session: ${error.message}`);
    }
    
    return data.id;
  }

  // Complete import session with final results
  async completeImportSession(
    sessionId: string,
    results: {
      totalRows: number;
      createdCount: number;
      updatedCount: number;
      errorCount: number;
      duration_ms: number;
    }
  ): Promise<void> {
    const status = results.errorCount === results.totalRows ? 'failed' : 'completed';
    
    const { error } = await supabase
      .from('import_sessions')
      .update({
        status,
        processed_rows: results.totalRows,
        created_count: results.createdCount,
        updated_count: results.updatedCount,
        error_count: results.errorCount,
        duration_ms: results.duration_ms,
        completed_at: new Date().toISOString()
      })
      .eq('id', sessionId);

    if (error) {
      throw new Error(`Failed to complete import session: ${error.message}`);
    }
  }

  // Log import errors
  async logImportErrors(
    sessionId: string,
    errors: Array<{
      row: number;
      message: string;
      data: Record<string, any>;
    }>
  ): Promise<void> {
    if (errors.length === 0) return;

    const errorRecords = errors.map(error => ({
      session_id: sessionId,
      row_number: error.row,
      error_type: 'validation',
      error_message: error.message,
      row_data: error.data
    }));

    const { error } = await supabase
      .from('import_errors')
      .insert(errorRecords);

    if (error) throw error;
  }

  // Get import sessions for the current user
  async getImportSessions(): Promise<ImportSession[]> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) throw new Error('User not authenticated');

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('id')
      .eq('uid', user.user.id)
      .single();

    if (profileError) {
      console.error('Profile lookup error:', profileError);
      throw new Error(`Failed to lookup user profile: ${profileError.message}`);
    }

    if (!profile) {
      console.error('No profile found for user:', user.user.id);
      throw new Error('User profile not found');
    }

    const { data, error } = await supabase
      .from('import_sessions')
      .select('*')
      .eq('imported_by', profile.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data.map(session => ({
      id: session.id,
      filename: session.filename,
      filePath: session.filename, // Use filename since file_path was removed
      modelType: session.model_type as ImportModelType,
      importedBy: session.imported_by,
      status: session.status as 'completed' | 'failed',
      totalRows: session.total_rows,
      processedRows: session.processed_rows,
      createdCount: session.created_count,
      updatedCount: session.updated_count,
      errorCount: session.error_count,
      errorDetails: session.error_details,
      createdAt: session.created_at,
      completedAt: session.completed_at,
      duration: session.duration_ms,
      progress: session.total_rows > 0 ? (session.processed_rows / session.total_rows) * 100 : 0
    }));
  }

  // Get import session with detailed errors
  async getImportSessionWithErrors(sessionId: string): Promise<{
    session: ImportSession;
    errors: Array<{
      id: string;
      row_number: number;
      error_type: string;
      error_message: string;
      row_data: any;
      created_at: string;
    }>;
  }> {
    const { data, error } = await supabase
      .rpc('get_import_session_with_errors', { session_uuid: sessionId });

    if (error) {
      console.error('Failed to get session with errors:', error);
      throw error;
    }

    if (!data?.session) {
      throw new Error('Session not found');
    }

    // Transform the session data to match our interface
    const session: ImportSession = {
      id: data.session.id,
      filename: data.session.filename,
      filePath: data.session.filename,
      modelType: data.session.model_type as ImportModelType,
      importedBy: data.session.imported_by,
      status: data.session.status as 'completed' | 'failed',
      totalRows: data.session.total_rows,
      processedRows: data.session.processed_rows,
      createdCount: data.session.created_count,
      updatedCount: data.session.updated_count,
      errorCount: data.session.error_count,
      errorDetails: data.session.error_details,
      createdAt: data.session.created_at,
      completedAt: data.session.completed_at,
      duration: data.session.duration_ms,
      progress: data.session.total_rows > 0 ? (data.session.processed_rows / data.session.total_rows) * 100 : 0
    };

    return {
      session,
      errors: data.errors || []
    };
  }

  // Import categories batch
  async importCategoriesBatch(rows: any[]): Promise<ImportBatchResult> {
    let created = 0;
    let updated = 0;
    const errors: Array<{ row: number; message: string; data: Record<string, any> }> = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 1;

      try {
        const categoryData = {
          name: row.Name || row.name,
          name_mz: row['Name (Mizo)'] || row.name_mz,
          icon: row.Icon || row.icon,
          position: row.Position ? parseInt(row.Position) : undefined,
          is_active: row['Is Active'] !== undefined ? 
            (typeof row['Is Active'] === 'boolean' ? row['Is Active'] : row['Is Active'] === 'true') : 
            true
        };

        if (row.ID || row.id) {
          // Update existing category
          const { error } = await supabase
            .from('categories')
            .update(categoryData)
            .eq('id', row.ID || row.id);

          if (error) throw error;
          updated++;
        } else {
          // Create new category
          const { error } = await supabase
            .from('categories')
            .insert(categoryData);

          if (error) throw error;
          created++;
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message: this.createFriendlyErrorMessage(error, row, 'categories'),
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      created,
      updated,
      errors
    };
  }

  // Import people batch
  async importPeopleBatch(rows: any[]): Promise<ImportBatchResult> {
    let created = 0;
    let updated = 0;
    const errors: Array<{ row: number; message: string; data: Record<string, any> }> = [];

    // First, fetch all available roles for mapping
    const { data: availableRoles, error: rolesError } = await supabase
      .from('people_roles')
      .select('id, name')
      .eq('is_active', true);

    if (rolesError) {
      throw new Error(`Failed to fetch available roles: ${rolesError.message}`);
    }

    const rolesMap = new Map(availableRoles.map(role => [role.name.toLowerCase(), role.id]));

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 1;

      try {
        const personData = {
          name: row.Name || row.name,
          email: row.Email || row.email,
          bio: row.Bio || row.bio,
          avatar_url: row['Avatar URL'] || row.avatarUrl || row.avatar_url
        };

        // Validate required fields
        if (!personData.name) {
          throw new Error('Name is required');
        }

        let personId: number;
        const existingId = row.ID || row.id;

        if (existingId) {
          // Update existing person
          const { data: updatedPerson, error } = await supabase
            .from('people')
            .update(personData)
            .eq('id', existingId)
            .select('id')
            .single();

          if (error) throw error;
          
          if (!updatedPerson) {
            throw new Error(`Person with ID ${existingId} not found`);
          }
          
          personId = updatedPerson.id;
          updated++;
        } else {
          // Create new person
          const { data: newPerson, error } = await supabase
            .from('people')
            .insert(personData)
            .select('id')
            .single();

          if (error) throw error;
          personId = newPerson.id;
          created++;
        }

        // Handle roles if provided
        const rolesString = row.Roles || row.roles;
        if (rolesString && typeof rolesString === 'string') {
          // Parse comma-separated roles
          const roleNames = rolesString.split(',').map(name => name.trim()).filter(name => name);
          
          if (roleNames.length > 0) {
            // First, remove existing roles for this person
            const { error: deleteError } = await supabase
              .from('people_to_roles')
              .delete()
              .eq('person_id', personId);

            if (deleteError) {
              console.warn(`Failed to remove existing roles for person ${personId}:`, deleteError);
            }

            // Map role names to IDs
            const roleIds: number[] = [];
            const invalidRoles: string[] = [];

            roleNames.forEach(roleName => {
              const roleId = rolesMap.get(roleName.toLowerCase());
              if (roleId) {
                roleIds.push(roleId);
              } else {
                invalidRoles.push(roleName);
              }
            });

            // Warn about invalid roles but don't fail the import
            if (invalidRoles.length > 0) {
              const availableRoles = Array.from(rolesMap.keys()).map(role => 
                role.charAt(0).toUpperCase() + role.slice(1)
              ).join(', ');
              console.warn(`Row ${rowNumber}: Unknown roles: ${invalidRoles.join(', ')}. Available roles: ${availableRoles}`);
              
              // Log warning for user (but don't add to errors to avoid failing the import)
              console.info(`Row ${rowNumber}: Person "${personData.name}" created/updated successfully, but some roles were ignored.`);
            }

            // Insert valid roles
            if (roleIds.length > 0) {
              const roleEntries = roleIds.map(roleId => ({
                person_id: personId,
                role_id: roleId
              }));

              const { error: rolesInsertError } = await supabase
                .from('people_to_roles')
                .insert(roleEntries);

              if (rolesInsertError) {
                throw new Error(`Failed to assign roles: ${rolesInsertError.message}`);
              }
            }
          }
        }

      } catch (error) {
        console.error(`Error importing person row ${rowNumber}:`, error);
        
        // Enhanced debugging for development
        if (error instanceof Error) {
          console.error('Full error details:', {
            message: error.message,
            stack: error.stack,
            rowData: row,
            rowNumber: rowNumber
          });
        }
        
        errors.push({
          row: rowNumber,
          message: this.createFriendlyErrorMessage(error, row, 'people'),
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      created,
      updated,
      errors
    };
  }

  // Helper function to resolve author names to person IDs with optional per-book bios
  private async resolveAuthorNames(
    authorNames: string[],
    authorBios?: string[]
  ): Promise<{ resolved: Array<{ id: number; bio: string | null }>; missing: string[] }> {
    if (!authorNames || authorNames.length === 0) {
      return { resolved: [], missing: [] };
    }

    const resolved: Array<{ id: number; bio: string | null }> = [];
    const missing: string[] = [];

    for (let i = 0; i < authorNames.length; i++) {
      const trimmedName = authorNames[i].trim();
      if (!trimmedName) continue;

      // Find person by name (case-insensitive)
      const { data: person, error } = await supabase
        .from('people')
        .select('id')
        .ilike('name', trimmedName)
        .single();

      if (error || !person) {
        missing.push(trimmedName);
      } else {
        const bio = authorBios && i < authorBios.length && authorBios[i].trim()
          ? authorBios[i].trim()
          : null;
        resolved.push({ id: person.id, bio });
      }
    }

    return { resolved, missing };
  }

  // Helper function to resolve category names to category IDs
  private async resolveCategoryNames(categoryNames: string[]): Promise<{ resolved: number[]; missing: string[] }> {
    if (!categoryNames || categoryNames.length === 0) {
      return { resolved: [], missing: [] };
    }

    const resolved: number[] = [];
    const missing: string[] = [];

    for (const categoryName of categoryNames) {
      const trimmedName = categoryName.trim();
      if (!trimmedName) continue;

      // Find category by name (case-insensitive)
      const { data: category, error } = await supabase
        .from('categories')
        .select('id')
        .ilike('name', trimmedName)
        .single();

      if (error || !category) {
        missing.push(trimmedName);
      } else {
        resolved.push(category.id);
      }
    }

    return { resolved, missing };
  }

  // Helper function to parse comma-separated values
  private parseCommaSeparatedValues(value: string | undefined): string[] {
    if (!value || typeof value !== 'string') return [];
    return value.split(',').map(item => item.trim()).filter(item => item.length > 0);
  }

  // Helper function to resolve a person name to person ID
  private async resolvePersonName(personName: string | undefined): Promise<number | null> {
    if (!personName || typeof personName !== 'string') {
      return null;
    }

    const trimmedName = personName.trim();
    if (!trimmedName) return null;

    // Find person by name (case-insensitive)
    const { data: person, error } = await supabase
      .from('people')
      .select('id')
      .ilike('name', trimmedName)
      .single();

    if (error || !person) {
      return null;
    }

    return person.id;
  }

  // Import books batch
  async importBooksBatch(rows: any[]): Promise<ImportBatchResult> {
    let created = 0;
    let updated = 0;
    const errors: Array<{ row: number; message: string; data: Record<string, any> }> = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 1;

      try {
        const bookData = {
          title: row.Title || row.title,
          cover_image: row['Cover Image'] || row.cover_image,
          cover_bg_color: row['Cover Background Color'] || row.cover_bg_color,
          reading_time: row['Reading Time'] ? parseInt(row['Reading Time']) : undefined,
          rating: row.Rating ? parseFloat(row.Rating) : undefined,
          is_active: row['Is Active'] !== undefined ? 
            (typeof row['Is Active'] === 'boolean' ? row['Is Active'] : row['Is Active'] === 'true') : 
            true
        };

        // Validate required fields
        if (!bookData.title) {
          throw new Error('Title is required');
        }

        let bookId: number;
        const existingId = row.ID || row.id;

        if (existingId) {
          // Update existing book
          const { data: updatedBook, error } = await supabase
            .from('books')
            .update(bookData)
            .eq('id', existingId)
            .select('id')
            .single();

          if (error) throw error;
          
          if (!updatedBook) {
            throw new Error(`Book with ID ${existingId} not found`);
          }
          
          bookId = updatedBook.id;
          updated++;

          // Clear existing relationships for updates
          await Promise.all([
            supabase.from('books_authors').delete().eq('book_id', bookId),
            supabase.from('books_categories').delete().eq('book_id', bookId)
          ]);
        } else {
          // Create new book
          const { data: newBook, error } = await supabase
            .from('books')
            .insert(bookData)
            .select('id')
            .single();

          if (error) throw error;
          bookId = newBook.id;
          created++;
        }

        // Handle authors relationships
        const authorNames = this.parseCommaSeparatedValues(row.Authors || row.authors);
        if (authorNames.length > 0) {
          // Parse pipe-delimited author bios
          const authorBiosRaw = row['Author Bios'] || row.author_bios || '';
          const authorBios = typeof authorBiosRaw === 'string' && authorBiosRaw.trim()
            ? authorBiosRaw.split('|').map((b: string) => b.trim())
            : [];

          const { resolved, missing: missingAuthors } = await this.resolveAuthorNames(authorNames, authorBios);

          // Insert author relationships with optional bios
          if (resolved.length > 0) {
            const authorRelationships = resolved.map(entry => ({
              book_id: bookId,
              person_id: entry.id,
              bio: entry.bio
            }));

            const { error: authorsError } = await supabase
              .from('books_authors')
              .insert(authorRelationships);

            if (authorsError) {
              throw new Error(`Failed to link authors: ${authorsError.message}`);
            }
          }

          // Report missing authors as warnings (but don't fail the import)
          if (missingAuthors.length > 0) {
            console.warn(`Row ${rowNumber}: Authors not found: ${missingAuthors.join(', ')}`);
          }
        }

        // Handle categories relationships
        const categoryNames = this.parseCommaSeparatedValues(row.Categories || row.categories);
        if (categoryNames.length > 0) {
          const { resolved: categoryIds, missing: missingCategories } = await this.resolveCategoryNames(categoryNames);
          
          // Insert category relationships
          if (categoryIds.length > 0) {
            const categoryRelationships = categoryIds.map(categoryId => ({
              book_id: bookId,
              category_id: categoryId
            }));

            const { error: categoriesError } = await supabase
              .from('books_categories')
              .insert(categoryRelationships);

            if (categoriesError) {
              throw new Error(`Failed to link categories: ${categoriesError.message}`);
            }
          }

          // Report missing categories as warnings (but don't fail the import)
          if (missingCategories.length > 0) {
            console.warn(`Row ${rowNumber}: Categories not found: ${missingCategories.join(', ')}`);
          }
        }

      } catch (error) {
        errors.push({
          row: rowNumber,
          message: this.createFriendlyErrorMessage(error, row, 'books'),
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      created,
      updated,
      errors
    };
  }

  // Import summary contents batch
  async importSummaryContentsBatch(rows: any[]): Promise<ImportBatchResult> {
    let created = 0;
    let updated = 0;
    const errors: Array<{ row: number; message: string; data: Record<string, any> }> = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 1;

      try {
        // First, find the book by title
        const bookTitle = row['Book Title'] || row.book_title;
        if (!bookTitle) {
          throw new Error('Book Title is required');
        }

        const { data: book, error: bookError } = await supabase
          .from('books')
          .select('id')
          .eq('title', bookTitle)
          .single();

        if (bookError || !book) {
          throw new Error(`Book not found: ${bookTitle}`);
        }

        // Resolve translator name to person ID
        const translatorName = row.Translator || row.translator;
        const translatorId = translatorName ? await this.resolvePersonName(translatorName) : null;

        // Resolve audio narrator name to person ID
        const narratorName = row['Audio Narrator'] || row.audio_narrator;
        const narratorId = narratorName ? await this.resolvePersonName(narratorName) : null;

        // Resolve content type (default to 'summary')
        const rawContentType = row['Content Type'] || row.content_type;
        const contentType = rawContentType && ['summary', 'full_book'].includes(String(rawContentType).toLowerCase())
          ? String(rawContentType).toLowerCase()
          : 'summary';

        const contentData = {
          book_id: book.id,
          language: row.Language || row.language,
          content_type: contentType,
          subtitle: row.Subtitle || row.subtitle,
          short_description: row['Short Description'] || row.short_description,
          audio_title: row['Audio Title'] || row.audio_title,
          audio_narrator: narratorId,
          audio_duration: row['Audio Duration'] ? parseInt(row['Audio Duration']) : undefined,
          audio_url: row['Audio URL'] || row.audio_url,
          translator: translatorId,
          position: row.Position ? parseInt(row.Position) : undefined
        };

        // Warn if translator or narrator names were provided but not found
        if (translatorName && !translatorId) {
          console.warn(`Row ${rowNumber}: Translator "${translatorName}" not found`);
        }
        if (narratorName && !narratorId) {
          console.warn(`Row ${rowNumber}: Audio Narrator "${narratorName}" not found`);
        }

        if (row.ID || row.id) {
          // Update existing content
          const { error } = await supabase
            .from('summary_contents')
            .update(contentData)
            .eq('id', row.ID || row.id);

          if (error) throw error;
          updated++;
        } else {
          // Create new content
          const { error } = await supabase
            .from('summary_contents')
            .insert(contentData);

          if (error) throw error;
          created++;
        }
      } catch (error) {
        errors.push({
          row: rowNumber,
          message: this.createFriendlyErrorMessage(error, row, 'summary_contents'),
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      created,
      updated,
      errors
    };
  }

  // Import chapters batch
  async importChaptersBatch(rows: any[]): Promise<ImportBatchResult> {
    let created = 0;
    let updated = 0;
    const errors: Array<{ row: number; message: string; data: Record<string, any> }> = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const rowNumber = i + 1;

      try {
        // Find the summary content by book title and language
        const bookTitle = row['Book Title'] || row.book_title;
        const language = row.Language || row.language;

        if (!bookTitle || !language) {
          throw new Error('Book Title and Language are required');
        }

        // First, find the book by title
        const { data: book, error: bookError } = await supabase
          .from('books')
          .select('id')
          .eq('title', bookTitle)
          .single();

        if (bookError || !book) {
          throw new Error(`Book not found: ${bookTitle}`);
        }

        // Then, find the summary content for this book and language
        const { data: summaryContent, error: contentError } = await supabase
          .from('summary_contents')
          .select('id, language')
          .eq('book_id', book.id)
          .eq('language', language)
          .single();

        if (contentError || !summaryContent) {
          // Get available languages for this book to provide better error message
          const { data: availableContents } = await supabase
            .from('summary_contents')
            .select('language')
            .eq('book_id', book.id);
          
          const availableLanguages = availableContents?.map(c => c.language).join(', ') || 'none';
          throw new Error(`Summary content not found for book: ${bookTitle} (${language}). Available languages: ${availableLanguages}`);
        }

        const chapterData = {
          summary_content_id: summaryContent.id,
          label: row.Label || row.label,
          title: row.Title || row.title,
          content: row.Content || row.content,
          start_time: row['Start Time'] ? parseInt(row['Start Time']) : undefined,
          position: row.Position ? parseInt(row.Position) : undefined
        };

        if (row.ID || row.id) {
          // Update existing chapter
          const { error } = await supabase
            .from('chapters')
            .update(chapterData)
            .eq('id', row.ID || row.id);

          if (error) throw error;
          updated++;
        } else {
          // Create new chapter
          const { error } = await supabase
            .from('chapters')
            .insert(chapterData);

          if (error) throw error;
          created++;
        }
      } catch (error) {
        console.error(`Error importing chapter row ${rowNumber}:`, error);
        
        // Enhanced debugging for development
        if (error instanceof Error) {
          console.error('Chapter import error details:', {
            message: error.message,
            bookTitle: row['Book Title'] || row.book_title,
            language: row.Language || row.language,
            rowData: row,
            rowNumber: rowNumber
          });
        }
        
        errors.push({
          row: rowNumber,
          message: this.createFriendlyErrorMessage(error, row, 'chapters'),
          data: row
        });
      }
    }

    return {
      success: errors.length === 0,
      created,
      updated,
      errors
    };
  }

  // Main import function
  async importBatch(
    modelType: ImportModelType,
    rows: any[],
    config: ImportConfig
  ): Promise<ImportBatchResult> {
    if (config.validateOnly) {
      // For validation only, just return success without actual import
      return {
        success: true,
        created: 0,
        updated: 0,
        errors: []
      };
    }

    switch (modelType) {
      case 'categories':
        return this.importCategoriesBatch(rows);
      case 'people':
        return this.importPeopleBatch(rows);
      case 'books':
        return this.importBooksBatch(rows);
      case 'summary_contents':
        return this.importSummaryContentsBatch(rows);
      case 'chapters':
        return this.importChaptersBatch(rows);
      default:
        throw new Error(`Unsupported model type: ${modelType}`);
    }
  }

  // Process complete import
  async processImport(
    previewData: ImportPreviewData,
    config: ImportConfig,
    filename: string,
    onProgress?: (progress: number, currentBatch: number, totalBatches: number) => void
  ): Promise<ImportResult> {
    const startTime = Date.now();
    
    // Filter valid rows
    const validRows = previewData.rows.filter((_, index) => {
      const rowNumber = index + 2;
      return !previewData.validation.errors.some(error => error.rowNumber === rowNumber);
    });

    // Detect model type
    const modelType = previewData.headers.includes('Name (Mizo)') ? 'categories' :
                     previewData.headers.includes('Email') && previewData.headers.includes('Bio') ? 'people' :
                     previewData.headers.includes('Reading Time') ? 'books' :
                     previewData.headers.includes('Short Description') && previewData.headers.includes('Book Title') ? 'summary_contents' :
                     'chapters' as ImportModelType;

    // Create import session (starts with status 'failed')
    const sessionId = await this.createImportSession(filename, modelType, validRows.length);

    // Process in batches
    const batches = Math.ceil(validRows.length / config.batchSize);
    let totalCreated = 0;
    let totalUpdated = 0;
    const allErrors: any[] = [];

    try {
      for (let batchIndex = 0; batchIndex < batches; batchIndex++) {
        const batchStart = batchIndex * config.batchSize;
        const batchEnd = Math.min(batchStart + config.batchSize, validRows.length);
        const batchRows = validRows.slice(batchStart, batchEnd);

        const batchResult = await this.importBatch(modelType, batchRows, config);
        
        totalCreated += batchResult.created;
        totalUpdated += batchResult.updated;
        allErrors.push(...batchResult.errors);

        if (onProgress) {
          const progress = ((batchIndex + 1) / batches) * 100;
          onProgress(progress, batchIndex + 1, batches);
        }
      }

      // Log errors to database BEFORE completing session
      if (allErrors.length > 0) {
        await this.logImportErrors(sessionId, allErrors);
      }

      // Complete the session with final results
      const duration = Date.now() - startTime;
      await this.completeImportSession(sessionId, {
        totalRows: validRows.length,
        createdCount: totalCreated,
        updatedCount: totalUpdated,
        errorCount: allErrors.length,
        duration_ms: duration
      });

      return {
        sessionId,
        success: allErrors.length < validRows.length, // Success if at least one row succeeded
        totalRows: validRows.length,
        createdCount: totalCreated,
        updatedCount: totalUpdated,
        errorCount: allErrors.length,
        errors: allErrors,
        duration
      };

    } catch (error) {
      // Session will remain as 'failed' status (default)
      console.error('Import process failed:', error);
      throw error;
    }
  }

  // Validate book dependencies before import
  async validateBookDependencies(rows: any[]): Promise<{
    valid: boolean;
    errors: Array<{
      row: number;
      message: string;
      missingAuthors: string[];
      missingCategories: string[];
    }>;
  }> {
    const errors: Array<{
      row: number;
      message: string;
      missingAuthors: string[];
      missingCategories: string[];
    }> = [];

    // Get all unique author and category names from the rows
    const allAuthorNames = new Set<string>();
    const allCategoryNames = new Set<string>();
    
    rows.forEach((row) => {
      const authorNames = this.parseCommaSeparatedValues(row.Authors || row.authors);
      const categoryNames = this.parseCommaSeparatedValues(row.Categories || row.categories);
      
      authorNames.forEach(name => allAuthorNames.add(name.toLowerCase()));
      categoryNames.forEach(name => allCategoryNames.add(name.toLowerCase()));
    });

    // Fetch existing people and categories
    const [peopleResult, categoriesResult] = await Promise.all([
      supabase.from('people').select('name').in('name', Array.from(allAuthorNames)),
      supabase.from('categories').select('name').in('name', Array.from(allCategoryNames))
    ]);

    const existingPeople = new Set(
      (peopleResult.data || []).map(p => p.name.toLowerCase())
    );
    const existingCategories = new Set(
      (categoriesResult.data || []).map(c => c.name.toLowerCase())
    );

    // Check each row
    rows.forEach((row, index) => {
      const authorNames = this.parseCommaSeparatedValues(row.Authors || row.authors);
      const categoryNames = this.parseCommaSeparatedValues(row.Categories || row.categories);
      
      const missingAuthors = authorNames.filter(name => !existingPeople.has(name.toLowerCase()));
      const missingCategories = categoryNames.filter(name => !existingCategories.has(name.toLowerCase()));
      
      if (missingAuthors.length > 0 || missingCategories.length > 0) {
        const messages = [];
        if (missingAuthors.length > 0) {
          messages.push(`Authors not found: ${missingAuthors.join(', ')}`);
        }
        if (missingCategories.length > 0) {
          messages.push(`Categories not found: ${missingCategories.join(', ')}`);
        }
        
        errors.push({
          row: index + 1,
          message: messages.join('; '),
          missingAuthors,
          missingCategories
        });
      }
    });

    return {
      valid: errors.length === 0,
      errors
    };
  }

  // Validate chapter dependencies before import
  async validateChapterDependencies(rows: any[]): Promise<{
    valid: boolean;
    errors: Array<{
      row: number;
      message: string;
      bookTitle: string;
      language: string;
    }>;
  }> {
    const errors: Array<{
      row: number;
      message: string;
      bookTitle: string;
      language: string;
    }> = [];

    // Get all unique book/language combinations from the chapters
    const bookLanguagePairs = new Map<string, { bookTitle: string; language: string; rows: number[] }>();
    
    rows.forEach((row, index) => {
      const bookTitle = row['Book Title'] || row.book_title;
      const language = row.Language || row.language;
      
      if (bookTitle && language) {
        const key = `${bookTitle}:${language}`;
        if (!bookLanguagePairs.has(key)) {
          bookLanguagePairs.set(key, { bookTitle, language, rows: [] });
        }
        bookLanguagePairs.get(key)!.rows.push(index + 1);
      }
    });

    // Check each book/language combination
    for (const [key, { bookTitle, language, rows: rowNumbers }] of bookLanguagePairs) {
      try {
        // Check if book exists
        const { data: book, error: bookError } = await supabase
          .from('books')
          .select('id')
          .eq('title', bookTitle)
          .single();

        if (bookError || !book) {
          rowNumbers.forEach(rowNumber => {
            errors.push({
              row: rowNumber,
              message: `Book "${bookTitle}" not found`,
              bookTitle,
              language
            });
          });
          continue;
        }

        // Check if summary content exists for this book/language
        const { data: summaryContent, error: contentError } = await supabase
          .from('summary_contents')
          .select('id')
          .eq('book_id', book.id)
          .eq('language', language)
          .single();

        if (contentError || !summaryContent) {
          // Get available languages for better error message
          const { data: availableContents } = await supabase
            .from('summary_contents')
            .select('language')
            .eq('book_id', book.id);
          
          const availableLanguages = availableContents?.map(c => c.language).join(', ') || 'none';
          
          rowNumbers.forEach(rowNumber => {
            errors.push({
              row: rowNumber,
              message: `Summary content not found for book "${bookTitle}" in language "${language}". Available languages: ${availableLanguages}`,
              bookTitle,
              language
            });
          });
        }
      } catch (error) {
        rowNumbers.forEach(rowNumber => {
          errors.push({
            row: rowNumber,
            message: `Error validating dependencies: ${error instanceof Error ? error.message : 'Unknown error'}`,
            bookTitle,
            language
          });
        });
      }
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}

export const importAPI = new ImportAPI(); 